import React, { Component } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Form,
  FormGroup,
  Button,
} from "reactstrap";
import swal from "sweetalert2";

import LoadingOverlay from "../Components/LoadingOverlay";
import Photo from "../Components/Photo";
import { handleError } from "@/util/errorHandler";
import { dateFormatter } from "@/util/formatter";
import merchants from "@/api/merchants";

import MapContainer from "../Components/MapContainer";
import UpdateMerchantModal from "./UpdateMerchantModal";
import WorkingHours from "../Components/WorkingHours";

class MerchantDetails extends Component {
  state = {
    data: {},
    promo: null,
    logo: null,
    isUpdateMerchantModalOpen: false,
    isLoading: true,
    types: [],
  };

  loadMerchantInfo = async id => {
    try {
      const { data } = await merchants.getMerchantInfo(id);
      this.setState({ data });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadMerchantTypes = async id => {
    try {
      const { data } = await merchants.getMerchantTypes();

      this.setState({ types: data });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadPhotos = async () => {
    const { logoUuid, promoImageUuid, logo, promoImage } = this.state.data;

    if (logo) {
      this.setState({ logo });
    }

    if (promoImage) {
      this.setState({ promo: promoImage });
    }

    if ((!logo && logoUuid) || (!promoImage && promoImageUuid)) {
      try {
        this.setState({ isLoading: true });

        await Promise.all([
          this.loadLogoPhoto(logoUuid),
          this.loadPromoPhoto(promoImageUuid),
        ]);
      } catch (err) {
        console.error(err);
        if (err.status !== 404 && err.data) {
          handleError(err.data);
        }
      } finally {
        this.setState({ isLoading: false });
      }
    }
  };

  loadLogoPhoto = async uuid => {
    if (!uuid) return;
    const { bytes } = await merchants.getMerchantImage(uuid);
    this.setState({ logo: bytes });
  };

  loadPromoPhoto = async uuid => {
    if (!uuid) return;
    const { bytes } = await merchants.getMerchantImage(uuid);
    this.setState({ promo: bytes });
  };

  async componentDidMount() {
    const id = this.props.match.params.id;
    await this.loadMerchantInfo(id);
    await this.loadMerchantTypes();
    await this.loadPhotos();
  }

  onToggleUpdateMerchantModal = () => {
    const id = this.props.match.params.id;
    this.setState(({ isUpdateMerchantModalOpen }) => {
      if (isUpdateMerchantModalOpen) {
        this.loadMerchantInfo(id);
      }
      return {
        isUpdateMerchantModalOpen: !isUpdateMerchantModalOpen,
      };
    });
  };

  onToggleDeleteMerchant = async () => {
    const id = this.props.match.params.id;
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete merchant information?",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
      preConfirm: async () => {
        try {
          await merchants.deleteMerchant(id);
          await swal(
            `Merchant ${this.state.data.name} removed successfully`,
            `Merchant ${this.state.data.name} has been sent for synchronization. Usually, it takes 5-10 minutes. In case of synchronization issues, please check the status of the merchant creation request.`,
            "success",
          );
          this.props.history.push("/merchants");
        } catch (error) {
          await handleError(error);
        }
      },
    });
  };

  displayPhotos = () => {
    const { logo, promo } = this.state;
    if (!(logo || promo)) {
      return;
    }
    return (
      <FormGroup>
        <Row>
          {logo && (
            <Col sm="6">
              <Photo
                src={`data:image/png;base64,${logo}`}
                name="Logo"
                data-cy="logo-photo"
              />
            </Col>
          )}
          {promo && (
            <Col sm="6">
              <Photo
                src={`data:image/png;base64,${promo}`}
                name="Promo"
                data-cy="promo-photo"
              />
            </Col>
          )}
        </Row>
      </FormGroup>
    );
  };

  render() {
    const {
      name,
      type,
      phoneNumber,
      address,
      workSchedule,
      website,
      facebook,
      coordinates,
      createTime,
      createdBy,
      updateTime,
    } = this.state.data;

    return (
      <>
        <Card>
          <LoadingOverlay loading={this.state.isLoading} />
          <CardHeader data-cy="merchant-info">Merchant info</CardHeader>
          <CardBody>
            <Row className="justify-content-center">
              <Col>
                <Form className="form-horizontal">
                  <FormGroup row>
                    <Col md="3">
                      <Label>Name:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{name}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3">
                      <Label>Type:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{type}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup>
                    <Row>
                      <Col md="3">
                        <Label>Coordinates:</Label>
                      </Col>
                      {coordinates && (
                        <Col xs="12" md="9">
                          <Label>
                            latitude - {coordinates.lat}, longitude -{" "}
                            {coordinates.lng}
                          </Label>
                        </Col>
                      )}
                    </Row>
                    <Row
                      className="justify-content-center"
                      style={{
                        minHeight: "400px",
                        maxWidth: "100%",
                        position: "relative",
                      }}>
                      <Col xs="12" md="6">
                        <MapContainer coordinates={coordinates} name={name} />
                      </Col>
                    </Row>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3">
                      <Label>Address:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{address}</Label>
                    </Col>
                  </FormGroup>
                  {phoneNumber && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Phone number:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{phoneNumber}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {workSchedule && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Working hours:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <WorkingHours data={workSchedule} />
                      </Col>
                    </FormGroup>
                  )}
                  {website && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Website:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{website}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {facebook && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Facebook:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{facebook}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {this.displayPhotos()}
                  <FormGroup row>
                    <Col md="3">
                      <Label>Created time:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{dateFormatter(createTime)}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3">
                      <Label>Created by:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{createdBy}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3">
                      <Label>Updated time:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{dateFormatter(updateTime)}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row className="mb-4">
                    <Col xs="12" md="9">
                      <Button
                        style={{ marginRight: "20px" }}
                        color="success"
                        onClick={this.onToggleUpdateMerchantModal}>
                        <span>Update</span>
                      </Button>
                      <Button
                        color="primary"
                        onClick={this.onToggleDeleteMerchant}>
                        <span>Delete</span>
                      </Button>
                    </Col>
                  </FormGroup>
                </Form>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <UpdateMerchantModal
          isOpen={this.state.isUpdateMerchantModalOpen}
          onModalClose={this.onToggleUpdateMerchantModal}
          data={this.state.data}
          types={this.state.types.filter(type => type.status === "ACTIVE")}
          logoFile={this.state.logo}
          promoFile={this.state.promo}
        />
      </>
    );
  }
}

export default MerchantDetails;
